// helpDeskEnums.js - Constants and Helper Functions for Help Desk Module

// ==================== TICKET PRIORITY ====================
export const TicketPriority = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
};

export const TicketPriorityLabels = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical'
};

export const TicketPriorityColors = {
  LOW: 'success',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'error'
};

export const TicketPriorityOptions = [
  { value: 'LOW', label: 'Low', color: 'success' },
  { value: 'MEDIUM', label: 'Medium', color: 'info' },
  { value: 'HIGH', label: 'High', color: 'warning' },
  { value: 'CRITICAL', label: 'Critical', color: 'error' }
];

// ==================== TICKET STATUS ====================
export const TicketStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  ESCALATED: 'ESCALATED',
  PENDING_CUSTOMER: 'PENDING_CUSTOMER',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED'
};

export const TicketStatusLabels = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  ESCALATED: 'Escalated',
  PENDING_CUSTOMER: 'Pending Customer',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed'
};

export const TicketStatusColors = {
  OPEN: 'info',
  IN_PROGRESS: 'primary',
  ESCALATED: 'error',
  PENDING_CUSTOMER: 'warning',
  RESOLVED: 'success',
  CLOSED: 'default'
};

export const TicketStatusOptions = [
  { value: 'OPEN', label: 'Open', color: 'info' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'primary' },
  { value: 'ESCALATED', label: 'Escalated', color: 'error' },
  { value: 'PENDING_CUSTOMER', label: 'Pending Customer', color: 'warning' },
  { value: 'RESOLVED', label: 'Resolved', color: 'success' },
  { value: 'CLOSED', label: 'Closed', color: 'default' }
];

// ==================== TICKET SOURCE ====================
export const TicketSource = {
  EMAIL: 'EMAIL',
  PORTAL: 'PORTAL',
  PHONE: 'PHONE',
  CHAT: 'CHAT',
  MANUAL: 'MANUAL'
};

export const TicketSourceLabels = {
  EMAIL: 'Email',
  PORTAL: 'Portal',
  PHONE: 'Phone',
  CHAT: 'Chat',
  MANUAL: 'Manual'
};

export const TicketSourceOptions = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'PORTAL', label: 'Portal' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'CHAT', label: 'Chat' },
  { value: 'MANUAL', label: 'Manual' }
];

// ==================== ARTICLE VISIBILITY ====================
export const ArticleVisibility = {
  INTERNAL: 'INTERNAL',
  PUBLIC: 'PUBLIC'
};

export const ArticleVisibilityLabels = {
  INTERNAL: 'Internal Only',
  PUBLIC: 'Public'
};

export const ArticleVisibilityOptions = [
  { value: 'INTERNAL', label: 'Internal Only', description: 'Visible only to agents' },
  { value: 'PUBLIC', label: 'Public', description: 'Visible to customers' }
];

// ==================== ARTICLE STATUS ====================
export const ArticleStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED'
};

export const ArticleStatusLabels = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived'
};

export const ArticleStatusColors = {
  DRAFT: 'default',
  PUBLISHED: 'success',
  ARCHIVED: 'warning'
};

export const ArticleStatusOptions = [
  { value: 'DRAFT', label: 'Draft', color: 'default' },
  { value: 'PUBLISHED', label: 'Published', color: 'success' },
  { value: 'ARCHIVED', label: 'Archived', color: 'warning' }
];

// ==================== SLA STATUS ====================
export const SLAStatusColors = {
  on_track: 'success',
  warning: 'warning',
  breached: 'error',
  met: 'success',
  no_sla: 'default'
};

// ==================== HELPER FUNCTIONS ====================

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatTimeAgo = (dateString) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;

  return formatDate(dateString);
};

export const formatDuration = (hours) => {
  if (hours === null || hours === undefined) return '-';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${Math.round(hours)} hours`;
  return `${Math.round(hours / 24)} days`;
};

export const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${value}%`;
};

export const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};
